import { cn } from "@gettactic/helpers/src/css/cn";
import React from "react";
import { Button } from "../../primitives/button";
import { Skeleton } from "../../primitives/skeleton";

export interface SectionContentWrapperAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: "primary" | "secondary" | "ghost";
  disabled?: boolean;
}

export interface SectionContentWrapperProps {
  /** Page title */
  title?: React.ReactNode;
  /** Page description */
  description?: React.ReactNode;
  /** Primary action buttons displayed in the header */
  actions?: SectionContentWrapperAction[];
  /** Secondary action buttons displayed before primary actions */
  secondaryActions?: SectionContentWrapperAction[];
  /** Show loading spinner instead of content */
  isLoading?: boolean;
  /** Whether the content is empty */
  isEmpty?: boolean;
  /** Custom empty view component */
  emptyView?: React.ReactNode;
  /** Page content */
  children?: React.ReactNode;
  /** Additional class names */
  className?: string;
}

export const SectionContentWrapper: React.FC<SectionContentWrapperProps> = ({
  title,
  description,
  actions,
  secondaryActions,
  isLoading = false,
  isEmpty = false,
  emptyView,
  children,
  className
}) => {
  const hasActions =
    (actions && actions.length > 0) ||
    (secondaryActions && secondaryActions.length > 0);
  const hasHeader = title || description || hasActions;

  return (
    <div className={cn("flex h-full w-full flex-col", className)}>
      {hasHeader && (
        <div className="flex items-start justify-between gap-4 pb-4">
          <div className="flex flex-col gap-1">
            {title && (
              <h2 className="text-xl font-semibold text-[var(--color-foreground,#171717)]">{title}</h2>
            )}
            {description && (
              <p className="text-sm text-[var(--color-foreground,#171717)] opacity-60">{description}</p>
            )}
          </div>
          {hasActions && (
            <div className="flex items-center gap-2">
              {secondaryActions?.map((action, index) => (
                <Button
                  key={`secondary-${index}`}
                  onClick={action.onClick}
                  variant="outline"
                  size="sm"
                  iconLeft={action.icon}
                  disabled={action.disabled}
                >
                  {action.label}
                </Button>
              ))}
              {actions?.map((action, index) => (
                <Button
                  key={`primary-${index}`}
                  onClick={action.onClick}
                  variant={
                    action.variant === "ghost"
                      ? "ghost"
                      : action.variant === "secondary"
                        ? "outline"
                        : "default"
                  }
                  size="sm"
                  iconLeft={action.icon}
                  disabled={action.disabled}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex-1 pt-4">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-[35px] w-[200px]" />
          </div>
        ) : isEmpty && emptyView ? (
          emptyView
        ) : (
          children
        )}
      </div>
    </div>
  );
};
